import { Link } from "react-router-dom";
import { ArrowLeft, Question } from "@phosphor-icons/react";

const Th = ({ children }) => <th className="p-2 text-left text-xs text-muted-foreground border-b border-border">{children}</th>;
const Td = ({ children }) => <td className="p-2 text-sm border-b border-border align-top">{children}</td>;

export default function SapHelp() {
  return (
    <div data-testid="sap-help-page" className="max-w-3xl">
      <Link to="/settings" className="text-xs font-mono uppercase text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-3">
        <ArrowLeft size={12} /> Back to Settings
      </Link>
      <span className="label-caps">Work Instruction · WI-SAP-001 · Rev 1.1</span>
      <h1 className="text-2xl font-display font-extrabold mt-1 mb-1 flex items-center gap-2">
        <Question size={24} /> Creating Projects in SAP ByDesign
      </h1>
      <p className="text-sm text-muted-foreground mb-5">
        Who may perform: <strong>NPD Admins</strong> (any project) · <strong>Project creators</strong> (own projects).
        The pusher (NPD admin or creator) decides and selects the cost centre at push time.
      </p>

      <section className="mb-5">
        <h2 className="font-semibold text-sm uppercase tracking-wide mb-2">Purpose</h2>
        <p className="text-sm">Create a Radish project — with all its phases and tasks — as a project in SAP ByDesign, so material and costs can be booked against it. No manual entry in SAP is required.</p>
      </section>

      <section className="mb-5">
        <h2 className="font-semibold text-sm uppercase tracking-wide mb-2">Before you start</h2>
        <ul className="text-sm list-disc pl-5 space-y-1">
          <li>Project is <strong>published</strong> (not Draft) — drafts cannot be pushed</li>
          <li>Project name, start date and target production date are correct (these go to SAP)</li>
          <li>All phases and tasks are entered — <strong>anything added later will NOT sync to SAP</strong></li>
          <li>You know the correct NPD cost centre: <code className="font-mono bg-muted px-1">P1-NPD</code> (Plant 1) or <code className="font-mono bg-muted px-1">P2-NPD</code> (Plant 2)</li>
        </ul>
      </section>

      <section className="mb-5">
        <h2 className="font-semibold text-sm uppercase tracking-wide mb-2">Method A — Single project (normal daily use)</h2>
        <ol className="text-sm list-decimal pl-5 space-y-1">
          <li>Open the project page in Radish</li>
          <li>Click <strong>Create in SAP</strong> (top-right, next to Send Notifications)</li>
          <li>Select the cost centre — P1-NPD / P2-NPD — or type another valid one</li>
          <li>Leave <strong>"Release immediately"</strong> ticked (recommended) so material can be booked right away; untick only if the project should stay "In Planning" for review</li>
          <li>Click <strong>Create in SAP</strong> and wait for the confirmation toast</li>
          <li>Verify the green badge, e.g. <strong>SAP: COC8 · Released</strong>. If it shows <em>In Planning</em>, click <strong>Release in SAP</strong> when ready</li>
        </ol>
      </section>

      <section className="mb-5">
        <h2 className="font-semibold text-sm uppercase tracking-wide mb-2">Method B — Bulk push (many projects at once)</h2>
        <ol className="text-sm list-decimal pl-5 space-y-1">
          <li>Go to <Link to="/settings" className="underline">Settings → SAP tab</Link> (admin only)</li>
          <li>The table lists every published project not yet in SAP — click <strong>Refresh</strong> if needed</li>
          <li>Set <strong>Cost Centre for all</strong>, or type a per-project override in each row</li>
          <li>Keep <strong>"Release immediately"</strong> ticked unless projects must stay In Planning</li>
          <li>Tick the projects (or the header checkbox for all) → <strong>Push Selected to SAP</strong></li>
          <li>Watch the <strong>Result</strong> column: SAP ID + status per row, or an error message. Fix errors and re-push only the failed rows</li>
        </ol>
      </section>

      <section className="mb-5">
        <h2 className="font-semibold text-sm uppercase tracking-wide mb-2">What gets created in SAP</h2>
        <table className="w-full border border-border" style={{ borderRadius: "2px" }}>
          <thead><tr><Th>Radish</Th><Th>SAP</Th></tr></thead>
          <tbody>
            <tr><Td>Project (name, start, target date)</Td><Td>Project (Cost Collecting) + chosen cost centre</Td></tr>
            <tr><Td>Phase</Td><Td>Phase task — ID <code className="font-mono">&lt;SAPID&gt;-PH1</code>, <code className="font-mono">-PH2</code>…</Td></tr>
            <tr><Td>Task (with dates)</Td><Td>Task nested under its phase — ID <code className="font-mono">&lt;SAPID&gt;-T1</code>, <code className="font-mono">-T2</code>…</Td></tr>
          </tbody>
        </table>
        <p className="text-xs text-muted-foreground mt-1">SAP IDs are stored back on the Radish project, phases and tasks for reconciliation.</p>
      </section>

      <section className="mb-5">
        <h2 className="font-semibold text-sm uppercase tracking-wide mb-2">Rules &amp; cautions</h2>
        <ul className="text-sm list-disc pl-5 space-y-1">
          <li><strong>One push per project</strong> — duplicates are blocked; the SAP badge means it is already there</li>
          <li><strong>Released projects cannot be deleted</strong> in SAP, only Finished → Closed — never push test projects with "Release immediately" on</li>
          <li>Push is one-time, not a live sync: later Radish changes must be maintained in SAP manually</li>
          <li>If "SAP push failed" appears: retry once after a minute; if it persists, inform the system admin</li>
        </ul>
      </section>

      <section className="mb-8">
        <h2 className="font-semibold text-sm uppercase tracking-wide mb-2">Responsibilities</h2>
        <table className="w-full border border-border" style={{ borderRadius: "2px" }}>
          <thead><tr><Th>Role</Th><Th>Responsibility</Th></tr></thead>
          <tbody>
            <tr><Td>Project creator</Td><Td>Push own project after publishing; decide and select the cost centre</Td></tr>
            <tr><Td>NPD Admin</Td><Td>Bulk pushes, cost centre selection, releasing held-back projects, error follow-up</Td></tr>
          </tbody>
        </table>
      </section>
    </div>
  );
}
